import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import useHttpError from '../../../../hooks/useHttpError/useHttpError';
import useSnackBar from '../../../../hooks/useSnackBar/useSnackBar';

import { DropdownItem } from '../../../../components/Dropdown/DropdownModels';
import { CreateUserRequest, UserExistsRequest } from '../../../../models/UsersModels';
import { UemCompany } from '../../../../models/UemOnboardingModels';

import UsersService from '../../../../services/Users/UsersService';
import UemOnboardingService from '../../../../services/UemOnboarding/UemOnboardingService';

function useNewUser(onClose: () => void, onNewUserSuccess: () => void) {
  const [isLoading, setIsLoading] = useState(false);
  const [companyOptions, setCompanyOptions] = useState<DropdownItem[]>([]);
  const [companies, setCompanies] = useState<UemCompany[]>([]);
  const [banOptions, setBanOptions] = useState<DropdownItem[]>([]);
  const [t] = useTranslation();
  const snackBar = useSnackBar();
  const { getHttpReason } = useHttpError();

  useEffect(() => {
    UemOnboardingService.api.fetchCompanies().then(
      (response) => {
        setCompanies(response.data.companies);
        setCompanyOptions(
          response.data.companies.map((obj) => ({
            key: obj.id,
            value: obj.id,
            label: `${obj.name} ${obj.country}`,
          })),
        );
      },
      () => {
        snackBar.showSnackBar(t('pages.eSimManager.newUserDrawer.fetchCompaniesError'), 'error');
      },
    );
  }, []);

  /**
   * Creates a new user
   */
  const createUser = (requestParams: CreateUserRequest) => {
    setIsLoading(true);
    UsersService.api.createUser(requestParams).then(
      () => {
        setIsLoading(false);
        onClose();
        onNewUserSuccess();
        snackBar.showSnackBar(t('pages.eSimManager.newUserDrawer.addUserSuccess'), 'success');
      },
      (res) => {
        const error = getHttpReason(res.response.data, t('pages.eSimManager.newUserDrawer.addUserError'));
        snackBar.showSnackBar(error, 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Verifies if the user already exists
   */
  const userExists = async (requestParams: UserExistsRequest, createUserRequestParams: CreateUserRequest) => {
    setIsLoading(true);
    UsersService.api.userExists(requestParams).then(
      (response) => {
        setIsLoading(false);
        if (response.data.exists) {
          const error = getHttpReason(response.data, t('pages.eSimManager.newUserDrawer.userAlreadyExists'));
          snackBar.showSnackBar(error, 'error');
        } else {
          createUser(createUserRequestParams);
        }
      },
      (res) => {
        const error = getHttpReason(res.response.data, t('pages.eSimManager.newUserDrawer.userValidatingError'));
        snackBar.showSnackBar(error, 'error');
        setIsLoading(false);
      },
    );
  };

  /**
   * Update ban options when company is selected
   */
  const updateBanOptions = (companyId: string): DropdownItem[] => {
    const company = companies.find((c) => c.id === companyId);
    if (!company || !company.bans) {
      setBanOptions([]);
      return [];
    }

    const bans = company.bans.map((ban) => ({
      key: ban.ban,
      value: ban.ban,
      label: `${ban.name} (${ban.ban})`,
    }));
    setBanOptions(bans);
    return bans;
  };

  return {
    isLoading,
    companyOptions,
    banOptions,
    companies,
    userExists,
    createUser,
    updateBanOptions,
  };
}

export default useNewUser;
